import { PkilError } from "./errors.js";

const HEX = "0123456789abcdef";

export const toHex = (b: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < b.length; i++) {
    const v = b[i]!;
    s += HEX[v >> 4]! + HEX[v & 15]!;
  }
  return s;
};

export const fromHex = (s: string): Uint8Array => {
  const hex = s.startsWith("0x") || s.startsWith("0X") ? s.slice(2) : s;
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) throw new PkilError("ARGUMENT", `invalid hex string`);
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
};

export function concatBytes(...parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

export function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

export const U64_MAX = (1n << 64n) - 1n;
export const U32_MAX = 0xffff_ffff;

/** Growable little-endian byte writer. */
export class ByteWriter {
  #buf = new Uint8Array(256);
  #view = new DataView(this.#buf.buffer);
  #len = 0;

  get length(): number {
    return this.#len;
  }

  #reserve(n: number): void {
    if (this.#len + n <= this.#buf.length) return;
    let cap = this.#buf.length * 2;
    while (cap < this.#len + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.#buf.subarray(0, this.#len));
    this.#buf = next;
    this.#view = new DataView(next.buffer);
  }

  u8(v: number): this {
    this.#reserve(1);
    this.#buf[this.#len++] = v & 0xff;
    return this;
  }

  u32(v: number): this {
    this.#reserve(4);
    this.#view.setUint32(this.#len, v, true);
    this.#len += 4;
    return this;
  }

  u64(v: bigint): this {
    this.#reserve(8);
    this.#view.setBigUint64(this.#len, v, true);
    this.#len += 8;
    return this;
  }

  bytes(b: Uint8Array): this {
    this.#reserve(b.length);
    this.#buf.set(b, this.#len);
    this.#len += b.length;
    return this;
  }

  /** Unsigned LEB128. */
  leb128(v: bigint): this {
    if (v < 0n || v > U64_MAX) throw new PkilError("ARGUMENT", `LEB128 value out of u64 range: ${v}`);
    do {
      let byte = Number(v & 0x7fn);
      v >>= 7n;
      if (v !== 0n) byte |= 0x80;
      this.u8(byte);
    } while (v !== 0n);
    return this;
  }

  reset(): void {
    this.#len = 0;
  }

  finish(): Uint8Array {
    return this.#buf.slice(0, this.#len);
  }
}

/** Bounds-checked little-endian reader; every overrun throws PkilError("MALFORMED"). */
export class ByteReader {
  readonly #b: Uint8Array;
  readonly #view: DataView;
  #pos: number;

  constructor(b: Uint8Array, offset = 0) {
    this.#b = b;
    this.#view = new DataView(b.buffer, b.byteOffset, b.byteLength);
    this.#pos = offset;
  }

  get pos(): number {
    return this.#pos;
  }

  get remaining(): number {
    return this.#b.length - this.#pos;
  }

  #need(n: number, what: string): void {
    if (this.#pos + n > this.#b.length) throw new PkilError("MALFORMED", `truncated reading ${what} at byte ${this.#pos}`);
  }

  u8(what = "u8"): number {
    this.#need(1, what);
    return this.#b[this.#pos++]!;
  }

  u32(what = "u32"): number {
    this.#need(4, what);
    const v = this.#view.getUint32(this.#pos, true);
    this.#pos += 4;
    return v;
  }

  u64(what = "u64"): bigint {
    this.#need(8, what);
    const v = this.#view.getBigUint64(this.#pos, true);
    this.#pos += 8;
    return v;
  }

  bytes(n: number, what = "bytes"): Uint8Array {
    this.#need(n, what);
    const out = this.#b.slice(this.#pos, this.#pos + n);
    this.#pos += n;
    return out;
  }

  /** Unsigned LEB128, at most 10 bytes, canonical (no redundant trailing zero groups), within u64. */
  leb128(what = "varint"): bigint {
    let result = 0n;
    let shift = 0n;
    for (let i = 0; i < 10; i++) {
      const byte = this.u8(what);
      result |= BigInt(byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) {
        if (byte === 0 && i > 0) throw new PkilError("MALFORMED", `non-canonical ${what}`);
        if (result > U64_MAX) throw new PkilError("MALFORMED", `${what} exceeds u64`);
        return result;
      }
      shift += 7n;
    }
    throw new PkilError("MALFORMED", `${what} longer than 10 bytes`);
  }
}
